import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import React from 'react'

import { Form } from '@/app/component/form'
import type { Condition, PersonalizationType } from '@/app/types'

import QuotaConditionEditor from './quota-condition-editor'
import { QuotaMetadataFormValues, quotaMetadataSchema } from './quota-form-validation'

type CommonProps = {
  conditionGroups: Condition[]
  conditionsError: boolean
  conditionsSupported: boolean
  defaultValues: QuotaMetadataFormValues
  metadataDisabled: boolean
  personalizationList: PersonalizationType
  onConditionsChange: (groups: Condition[]) => void
}

type SingleQuotaFormProps = CommonProps & {
  id: string
  onDirtyChange?: (isDirty: boolean) => void
  onSubmit: (values: QuotaMetadataFormValues) => void | Promise<void>
}

type SharedQuotaFormProps = CommonProps & {
  isUpdating: boolean
  metadataHeader: React.ReactNode
  metadataUnlocked: boolean
  onCancelMetadata: () => void
  onSubmit: (values: QuotaMetadataFormValues) => void | Promise<void>
}

export const SingleQuotaForm: React.FC<SingleQuotaFormProps> = ({
  conditionGroups,
  conditionsError,
  conditionsSupported,
  defaultValues,
  id,
  metadataDisabled,
  personalizationList,
  onConditionsChange,
  onDirtyChange,
  onSubmit,
}) => {
  const fields = React.useMemo(() => getMetadataFields(metadataDisabled), [metadataDisabled])

  return (
    <>
      <Box sx={metadataContainerSx}>
        <Form
          defaultValues={defaultValues}
          fields={fields}
          id={id}
          schema={quotaMetadataSchema}
          shouldReset
          onSubmit={onSubmit}
          renderActions={
            onDirtyChange
              ? ({ isDirty }) => <DirtyStateReporter isDirty={isDirty} onDirtyChange={onDirtyChange} />
              : undefined
          }
        />
      </Box>
      <QuotaConditions
        conditionGroups={conditionGroups}
        conditionsError={conditionsError}
        conditionsSupported={conditionsSupported}
        personalizationList={personalizationList}
        onConditionsChange={onConditionsChange}
      />
    </>
  )
}

export const SharedQuotaForm: React.FC<SharedQuotaFormProps> = ({
  conditionGroups,
  conditionsError,
  conditionsSupported,
  defaultValues,
  isUpdating,
  metadataDisabled,
  metadataHeader,
  metadataUnlocked,
  personalizationList,
  onCancelMetadata,
  onConditionsChange,
  onSubmit,
}) => {
  const fields = React.useMemo(() => getMetadataFields(metadataDisabled), [metadataDisabled])
  const [isDirty, setIsDirty] = React.useState(false)

  return (
    <>
      <Box
        sx={{
          ...metadataContainerSx,
          bgcolor: 'action.hover',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          p: 2,
        }}
      >
        {metadataHeader}
        <Form
          defaultValues={defaultValues}
          fields={fields}
          id={SHARED_QUOTA_METADATA_FORM_ID}
          schema={quotaMetadataSchema}
          shouldReset
          onSubmit={onSubmit}
          renderActions={({ isDirty: formIsDirty }) => (
            <DirtyStateReporter isDirty={formIsDirty} onDirtyChange={setIsDirty} />
          )}
        />
        {metadataUnlocked && (
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5, mt: 2 }}>
            <Button
              disabled={isUpdating || !isDirty}
              form={SHARED_QUOTA_METADATA_FORM_ID}
              loading={isUpdating}
              type="submit"
              variant="contained"
            >
              Save name &amp; goal
            </Button>
            <Button disabled={isUpdating} onClick={onCancelMetadata}>
              Cancel
            </Button>
          </Box>
        )}
      </Box>
      <Box
        aria-label="Conditions"
        component="fieldset"
        disabled={metadataUnlocked}
        sx={getConditionsContainerSx(metadataUnlocked)}
      >
        <QuotaConditions
          conditionGroups={conditionGroups}
          conditionsError={conditionsError}
          conditionsSupported={conditionsSupported}
          personalizationList={personalizationList}
          onConditionsChange={onConditionsChange}
        />
      </Box>
    </>
  )
}

const QuotaConditions: React.FC<Omit<CommonProps, 'defaultValues' | 'metadataDisabled'>> = ({
  conditionGroups,
  conditionsError,
  conditionsSupported,
  personalizationList,
  onConditionsChange,
}) => (
  <>
    <Typography fontWeight={600} variant="subtitle1">
      Conditions
    </Typography>
    <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
      Which responses should count toward this quota? Note you need to run the “Mark Survey as Completed” action to
      evaluate a response.
    </Typography>
    {conditionsSupported ? (
      <QuotaConditionEditor
        error={conditionsError}
        groups={conditionGroups}
        personalizationList={personalizationList}
        onChange={onConditionsChange}
      />
    ) : (
      <Alert severity="warning">
        These legacy conditions cannot be edited here yet. They will be preserved unchanged.
      </Alert>
    )}
  </>
)

const SHARED_QUOTA_METADATA_FORM_ID = 'shared-quota-metadata-form'

const getMetadataFields = (disabled: boolean) =>
  ({
    name: {
      type: 'text',
      fieldProps: {
        ariaLabel: 'Quota name',
        disabled,
        info: ' ',
        label: 'Quota name',
        name: 'name',
        required: true,
        size: 'medium',
        sx: { mb: 0 },
      },
    },
    goal: {
      type: 'text',
      fieldProps: {
        ariaLabel: 'Goal',
        disabled,
        info: 'completed responses',
        inputMode: 'numeric',
        label: 'Goal',
        name: 'goal',
        required: true,
        size: 'medium',
        sx: { mb: 0 },
      },
    },
  }) as const

const metadataContainerSx = {
  '& form': {
    display: 'grid',
    gap: 3,
    gridTemplateColumns: { md: '1fr 240px', xs: '1fr' },
  },
  mb: 3,
}

const conditionsContainerSx = {
  border: 0,
  m: 0,
  minWidth: 0,
  p: 0,
}

const getConditionsContainerSx = (disabled: boolean) => ({
  ...conditionsContainerSx,
  opacity: disabled ? 0.5 : 1,
  pointerEvents: disabled ? 'none' : 'auto',
})

type DirtyStateReporterProps = {
  isDirty: boolean
  onDirtyChange: (isDirty: boolean) => void
}

const DirtyStateReporter: React.FC<DirtyStateReporterProps> = ({ isDirty, onDirtyChange }) => {
  React.useEffect(() => {
    if (!isDirty) {
      return // exit early to preserve the touched state even when the form values are reset to the default values.
    }
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  return null
}
